/**
 * Telemetry service: reports recovery, health, backup events.
 * Backend-agnostic, extensible for analytics.
 */

import type { TelemetryEvent } from '../health/types';
import { PWA_VERSION } from '../../../pwa-version';

const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export interface TelemetryConfig {
  enabled: boolean;
  endpoint: string;
  batchSize: number;
  flushIntervalMs: number;
}

const WORKER_HEALTH_DEBOUNCE_MS = 10_000;

class TelemetryServiceImpl {
  private _config: TelemetryConfig = {
    enabled: false,
    endpoint: '',
    batchSize: 10,
    flushIntervalMs: 30000,
  };
  private _queue: TelemetryEvent[] = [];
  private _flushIntervalId: ReturnType<typeof setInterval> | null = null;
  private _workerHealthLastEmit = new Map<string, number>();
  /** Dopo il primo flush fallito (rete/CORS/5xx) non ritentare: evita flood in console e richieste ripetute. */
  private _transportDisabled = false;

  configure(config: Partial<TelemetryConfig>): void {
    this._config = { ...this._config, ...config };
  }

  /** True se il trasporto è stato disabilitato dopo errore (non si riattiva fino a reload). */
  isTransportDisabled(): boolean {
    return this._transportDisabled;
  }

  setEnabled(enabled: boolean): void {
    this._config.enabled = enabled;
    if (enabled) {
      this._startFlushInterval();
    } else {
      this._stopFlushInterval();
    }
  }

  start(): void {
    if (this._config.enabled) {
      this._startFlushInterval();
    }
  }

  async stop(): Promise<void> {
    this._stopFlushInterval();
    await this.flush();
  }

  track(event: Omit<TelemetryEvent, 'timestamp' | 'sessionId'>): void {
    if (!this._config.enabled) return;

    const fullEvent: TelemetryEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      sessionId: SESSION_ID,
    };

    this._queue.push(fullEvent);

    if (this._queue.length >= this._config.batchSize) {
      void this.flush().catch(() => {});
    }
  }

  trackError(error: Error | string, context?: Record<string, unknown>): void {
    this.track({
      type: 'error',
      data: {
        message: typeof error === 'string' ? error : error.message,
        stack: typeof error === 'string' ? undefined : (error as Error).stack,
        ...context,
      },
    });
  }

  reportRecovery(data: Record<string, unknown>): void {
    this.track({ type: 'recovery', data });
  }

  reportHealthChange(data: Record<string, unknown>): void {
    this.track({ type: 'health_check', data });
  }

  trackAppBoot(data: { hasData: boolean; hasAuth: boolean; isIOSPWA: boolean }): void {
    this.track({ type: 'app_boot', data });
  }

  trackEvictionDetected(data: { lastSeenAt?: string; dataLost: string[] }): void {
    this.track({ type: 'eviction_detected', data });
  }

  trackForcedRefresh(data: { lastVisibilityHidden?: number; duration: number }): void {
    this.track({ type: 'forced_refresh', data });
  }

  trackStoragePersist(data: { granted: boolean; isPersisted: boolean }): void {
    this.track({ type: 'storage_persist', data });
  }

  trackSyncPushFailed(data: { batchSize: number; error: string; attempts: number }): void {
    this.track({ type: 'sync_push_failed', data });
  }

  trackRecoveryFromJournal(data: { entriesRecovered: number; durationMs: number }): void {
    this.track({ type: 'recovery_from_journal', data });
  }

  /** Segmenti sync NDJSON vs flush SQLite vs COI (telemetria opzionale). */
  trackOfflineSyncTiming(data: Record<string, unknown> & { segment: string; durationMs: number }): void {
    this.track({ type: 'offline_sync_timing', data });
  }

  /**
   * Structured worker / SQLite health events for client-logs audit.
   * Debounced per event+reason to avoid flooding on crash loops.
   */
  trackWorkerHealth(
    event: 'crash' | 'restart' | 'repaired' | 'unrecoverable',
    ctx?: {
      restarts?: number;
      reason?: string;
      integrityOk?: boolean;
      phase?: string;
      [key: string]: unknown;
    }
  ): void {
    const reason = typeof ctx?.reason === 'string' ? ctx.reason : '';
    const key = `${event}:${reason}`;
    const now = Date.now();
    const last = this._workerHealthLastEmit.get(key) ?? 0;
    if (now - last < WORKER_HEALTH_DEBOUNCE_MS) {
      return;
    }
    this._workerHealthLastEmit.set(key, now);

    void this._enrichWorkerHealthPayload().then((enriched) => {
      this.track({
        type: 'worker_health',
        data: {
          event,
          ...enriched,
          ...ctx,
        },
      });
    });
  }

  private async _enrichWorkerHealthPayload(): Promise<Record<string, unknown>> {
    const enriched: Record<string, unknown> = {
      pwaVersion: PWA_VERSION,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      crossOriginIsolated:
        typeof crossOriginIsolated !== 'undefined' ? crossOriginIsolated : undefined,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        enriched.storageUsageBytes = est.usage ?? null;
        enriched.storageQuotaBytes = est.quota ?? null;
      }
    } catch {
      // ignore storage estimate failures
    }
    return enriched;
  }

  async flush(): Promise<void> {
    if (this._transportDisabled || this._queue.length === 0 || !this._config.endpoint) return;

    const events = [...this._queue];
    this._queue = [];

    try {
      const response = await fetch(this._config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      });

      if (!response.ok) {
        this._disableTransportAfterFailure();
        return;
      }
    } catch {
      this._disableTransportAfterFailure();
    }
  }

  private _disableTransportAfterFailure(): void {
    if (this._transportDisabled) return;
    this._transportDisabled = true;
    this._queue = [];
    this.setEnabled(false);
  }

  private _startFlushInterval(): void {
    if (this._flushIntervalId !== null) return;

    this._flushIntervalId = setInterval(
      () => void this.flush().catch(() => {}),
      this._config.flushIntervalMs
    );
  }

  private _stopFlushInterval(): void {
    if (this._flushIntervalId !== null) {
      clearInterval(this._flushIntervalId);
      this._flushIntervalId = null;
    }
  }
}

export const telemetryService = new TelemetryServiceImpl();
